import { useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import { apiErrorMessage } from '../../utils/apiError';
import { useDebouncedValue, useLatestRequest } from '../../components/admin/useAdminTable';
import ChapterCalendarSelector from '../../components/reporting/ChapterCalendarSelector';
import BookSelector from '../../components/reporting/BookSelector';
import { BIBLE_BOOKS } from '../../utils/bibleBooks';
import { submitLabel } from '../../utils/logSubmit';
import './AdminUndoReportsPage.css';
import '../ReportReadingPage.css';

const READER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const chapterIdsForBooks = (names) => {
  const ids = [];
  for (const name of names) {
    const book = BIBLE_BOOKS.find((item) => item.name === name);
    if (!book) continue;
    for (let n = 0; n < book.chapters; n += 1) ids.push(book.startId + n);
  }
  return ids;
};

const asReader = (reader) => {
  const id = typeof reader?.id === 'string' ? reader.id : '';
  if (!READER_ID.test(id)) return null;
  return {
    id,
    name: reader.name || 'Reader',
    isPrimary: Boolean(reader.isPrimary),
    isActive: reader.isActive !== false,
    reportCount: reader.reportCount || 0
  };
};

const AdminUndoReportsPage = () => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query.trim());
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [user, setUser] = useState(null);
  const [loadingReaders, setLoadingReaders] = useState(false);
  const [selectedReaders, setSelectedReaders] = useState([]);
  const [mode, setMode] = useState('chapters');
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const beginRequest = useLatestRequest();
  const beginReaderLoad = useLatestRequest();

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setMatches([]);
      setSearchError(null);
      setSearching(false);
      return undefined;
    }
    const request = beginRequest();
    setSearching(true);
    adminApi.getUsers({ search: debouncedQuery, limit: 12 })
      .then((result) => {
        if (!request.isCurrent()) return;
        setMatches(result.data.users || []);
        setSearchError(null);
      })
      .catch((err) => {
        if (!request.isCurrent()) return;
        setSearchError(apiErrorMessage(err, 'Could not search users.'));
        setMatches([]);
      })
      .finally(() => {
        if (request.isCurrent()) setSearching(false);
      });
    return undefined;
  }, [beginRequest, debouncedQuery]);

  const pickUser = (next) => {
    const request = beginReaderLoad();
    setUser({ ...next, readers: [] });
    setSelectedReaders([]);
    setLoadingReaders(true);
    setSelectedChapters([]);
    setSelectedBooks([]);
    setReason('');
    setNotice(null);
    setError(null);
    setQuery('');
    setMatches([]);
    adminApi.getReaders({ userId: next.id, limit: 100 })
      .then((result) => {
        if (!request.isCurrent()) return;
        const readers = (result.data.readers || []).map(asReader).filter(Boolean);
        setUser((current) => (current && current.id === next.id ? { ...current, readers } : current));
        setSelectedReaders(readers);
      })
      .catch((err) => {
        if (!request.isCurrent()) return;
        setError(apiErrorMessage(err, 'Could not load readers for that person.'));
      })
      .finally(() => {
        if (request.isCurrent()) setLoadingReaders(false);
      });
  };

  const clearUser = () => {
    beginReaderLoad();
    setUser(null);
    setLoadingReaders(false);
    setSelectedReaders([]);
    setSelectedChapters([]);
    setSelectedBooks([]);
    setNotice(null);
    setError(null);
  };

  const toggleReader = (reader) => {
    setSelectedReaders((current) => (
      current.some((item) => item.id === reader.id)
        ? current.filter((item) => item.id !== reader.id)
        : [...current, reader]
    ));
  };

  const loggedLabel = submitLabel({
    mode,
    chapterIds: selectedChapters,
    books: selectedBooks
  });
  const actionLabel = loggedLabel ? loggedLabel.replace(/^Log /, 'Remove ') : null;
  const canSubmit = Boolean(user) && !loadingReaders && selectedReaders.length > 0 && actionLabel && !submitting;

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    const chapterIds = mode === 'books'
      ? chapterIdsForBooks(selectedBooks)
      : selectedChapters;
    try {
      setSubmitting(true);
      setError(null);
      setNotice(null);
      const result = await adminApi.undoChapters({
        userId: user.id,
        readerIds: selectedReaders.map((reader) => reader.id),
        chapterIds,
        ...(reason.trim() ? { reason: reason.trim() } : {})
      });
      if (!result.data.undone) {
        setError(result.data.message || 'None of those chapters are on the selected readers.');
        return;
      }
      let remaining = null;
      try {
        const loaded = await adminApi.getReaders({ userId: user.id, limit: 100 });
        const readers = (loaded.data.readers || []).map(asReader).filter(Boolean);
        const selectedIds = new Set(selectedReaders.map((reader) => reader.id));
        setUser((current) => (current && current.id === user.id ? { ...current, readers } : current));
        setSelectedReaders(readers.filter((reader) => selectedIds.has(reader.id)));
        remaining = readers
          .filter((reader) => selectedIds.has(reader.id))
          .reduce((sum, reader) => sum + (reader.reportCount || 0), 0);
      } catch {
        remaining = null;
      }
      setNotice(remaining == null
        ? result.data.message
        : `${result.data.message} ${remaining} still on the selected readers.`);
      setSelectedChapters([]);
      setSelectedBooks([]);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not remove those chapters.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-undo-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Undo Reports</h1>
        <p className="admin-page-description">
          Find a person, then take chapters off their record the same way the app logs them.
        </p>
      </div>

      <div className="report-page undo-log">
        {!user && (
          <div className="undo-search">
            <label htmlFor="undo-user-search">Name or email</label>
            <input
              id="undo-user-search"
              value={query}
              placeholder="Search by name or email"
              autoComplete="off"
              onChange={(event) => setQuery(event.target.value)}
            />
            {searching && <p className="undo-search-status">Searching…</p>}
            {searchError && <p className="report-alert report-alert--error" role="alert">{searchError}</p>}
            {!searching && debouncedQuery.length >= 2 && matches.length === 0 && !searchError && (
              <p className="undo-search-status">No one matches that.</p>
            )}
            {matches.length > 0 && (
              <ul className="undo-user-list">
                {matches.map((match) => (
                  <li key={match.id}>
                    <button type="button" className="undo-user-card" onClick={() => pickUser(match)}>
                      <strong>{match.name}</strong>
                      <span>{match.email}</span>
                      <span className="undo-user-meta">
                        {match.totalReaders || 0} reader{(match.totalReaders || 0) === 1 ? '' : 's'}
                        {' · '}
                        {match.totalReports || 0} chapter{(match.totalReports || 0) === 1 ? '' : 's'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {user && (
          <>
            <div className="undo-chosen">
              <div>
                <strong>{user.name}</strong>
                <span>{user.email}</span>
                <span className="undo-user-meta">
                  {(user.readers || []).reduce((sum, reader) => sum + (reader.reportCount || 0), 0)} chapters on this account
                </span>
              </div>
              <button type="button" onClick={clearUser}>Change</button>
            </div>

            {notice && <p className="report-alert report-alert--success" role="status">{notice}</p>}
            {error && <p className="report-alert report-alert--error" role="alert">{error}</p>}

            {loadingReaders ? (
              <p className="undo-search-status">Loading readers…</p>
            ) : (user.readers || []).length === 0 ? (
              error ? null : <p className="report-alert report-alert--error">This account has no readers.</p>
            ) : (
              <form onSubmit={submit} className={`report-form ${submitting ? 'submitting' : ''}`}>
                {(user.readers || []).length > 1 && (
                  <div className="undo-readers" role="group" aria-label="Which readers">
                    {(user.readers || []).map((reader) => {
                      const on = selectedReaders.some((item) => item.id === reader.id);
                      return (
                        <button
                          key={reader.id}
                          type="button"
                          className={on ? 'is-on' : ''}
                          aria-pressed={on}
                          onClick={() => toggleReader(reader)}
                        >
                          {reader.name} · {reader.reportCount || 0}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="report-mode" role="tablist" aria-label="What should come off the record?">
                  {[['chapters', 'Chapters'], ['books', 'Whole books']].map(([key, text]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={mode === key}
                      className={`report-mode-tab ${mode === key ? 'active' : ''}`}
                      onClick={() => setMode(key)}
                    >
                      {text}
                    </button>
                  ))}
                </div>

                {mode === 'chapters' ? (
                  <ChapterCalendarSelector
                    selectedChapters={selectedChapters}
                    onChaptersChange={setSelectedChapters}
                  />
                ) : (
                  <BookSelector selectedBooks={selectedBooks} onBooksChange={setSelectedBooks} />
                )}

                <label className="undo-reason">
                  Reason
                  <textarea
                    value={reason}
                    maxLength={500}
                    rows={2}
                    placeholder="Optional. Saved on the admin audit log."
                    onChange={(event) => setReason(event.target.value)}
                  />
                </label>

                <div className="report-submit">
                  <button type="submit" className="report-submit-button" disabled={!canSubmit}>
                    {submitting ? 'Removing…' : actionLabel || 'Choose what to remove'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUndoReportsPage;
